/**
 * Quiz Component - Native JavaScript Web Component
 * Handles quiz logic, navigation, and result matching
 */
class QuizComponent extends HTMLElement {
  constructor() {
    super();
    this.currentQuestion = 1;
    this.questions = [];
    this.resultData = null;
    this.selectedOptions = [];
    // Bind event handlers once to enable proper removal
    this.boundHandleFocusIn = this.handleFocusIn.bind(this);
  }

  connectedCallback() {
    this.initializeQuiz();
    this.setupEventListeners();
  }

  disconnectedCallback() {
    this.removeEventListeners();
  }

  /**
   * Load result data from script tag
   * Can be called multiple times to refresh data
   */
  loadResultData() {
    const resultScript = this.querySelector('[data-quiz-results]');
    if (resultScript) {
      try {
        const scriptContent = resultScript.textContent.trim();
        if (!scriptContent) {
          console.warn('Quiz results script tag is empty');
          this.resultData = [];
        } else {
          const data = JSON.parse(scriptContent);
          this.resultData = data.results || [];
          if (!this.resultData || this.resultData.length === 0) {
            console.warn('Quiz results array is empty or missing');
          }
        }
      } catch (error) {
        console.error('Failed to parse quiz result data:', error);
        this.resultData = [];
      }
    } else {
      console.warn('Quiz results script tag not found');
      this.resultData = [];
    }
  }

  /**
   * Initialize quiz data and UI
   */
  initializeQuiz() {
    // Load result data from JSON script tag
    this.loadResultData();

    // Get all question fieldsets
    this.questions = Array.from(this.querySelectorAll('[data-question]'));
    
    // Initialize scroll-trigger animations for all questions
    // Add offscreen class initially, will be removed when question is shown
    this.questions.forEach((question, index) => {
      if (index === 0) {
        // First question should be visible and animated
        question.classList.remove('scroll-trigger--offscreen');
      } else {
        // Other questions start offscreen
        question.classList.add('scroll-trigger--offscreen');
      }
    });
    
    // Auto-select first checkbox for multi-select questions
    for (const question of this.questions) {
      if (question.hasAttribute('data-multi-select')) {
        const firstCheckbox = question.querySelector('input[type="checkbox"]');
        if (firstCheckbox && !firstCheckbox.checked) {
          firstCheckbox.checked = true;
        }
      }
    }
    
    // Set initial question
    this.setCurrentQuestion(1);
    
    // Initialize back button visibility
    this.toggleBackButtons();
  }

  /**
   * Setup event listeners
   */
  setupEventListeners() {
    // Back buttons - search in the entire document since button is in header
    const backButtons = document.querySelectorAll('[data-quiz-back]');
    for (const button of backButtons) {
      // Remove any existing listeners by cloning and replacing
      const buttonAny = /** @type {any} */ (button);
      if (buttonAny.parentNode) {
        const newButton = button.cloneNode(true);
        buttonAny.parentNode.replaceChild(newButton, button);
        
        newButton.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.goToQuestion(this.currentQuestion - 1);
          return false;
        });
      } else {
        // If no parent, just add listener directly
        button.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.goToQuestion(this.currentQuestion - 1);
          return false;
        });
      }
    }

    // Next buttons
    const nextButtons = this.querySelectorAll('[data-quiz-next]');
    for (const button of nextButtons) {
      button.addEventListener('click', () => {
        const nextIndex = parseInt(button.getAttribute('data-quiz-next') || '0');
        this.goToQuestion(nextIndex);
      });
    }

    // Submit button
    const submitButton = this.querySelector('[data-quiz-submit]');
    if (submitButton) {
      submitButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.handleSubmit();
        return false;
      });
    }

    // Form submission
    const form = this.querySelector('[data-quiz-form]');
    if (form) {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.handleSubmit();
        return false;
      });
    }

    // Track option selections
    const options = this.querySelectorAll('input[type="radio"], input[type="checkbox"]');
    for (const option of options) {
      option.addEventListener('change', () => {
        // Prevent unchecking last checkbox
        if (option.type === 'checkbox' && !option.checked) {
          const container = option.closest('.quiz-modal__options');
          const checkboxes = container?.querySelectorAll('.quiz-modal__input');

          if (checkboxes && ![...checkboxes].some(cb => cb.checked)) {
            option.checked = true;
            return; // stop further processing
          }
        }
        this.updateSelectedOptions();
        this.updateOptionStates();
        // Update Question 2 media if Question 1 changed
        const question = option.closest('[data-question]');
        if (question) {
          const questionNum = parseInt(question.getAttribute('data-question') || '0');
          if (questionNum === 1) {
            this.updateQuestion2Media();
          }
        }
      });
    }
    
    // Update initial option states
    this.updateOptionStates();
    
    // Update Question 2 media on initial load based on Question 1 selection
    this.updateQuestion2Media();

    // Focus tracking for question navigation
    document.addEventListener('focusin', this.boundHandleFocusIn);
  }

  /**
   * Remove event listeners
   */
  removeEventListeners() {
    document.removeEventListener('focusin', this.boundHandleFocusIn);
  }

  /**
   * Handle focus events to track current question
   */
  handleFocusIn(event) {
    const target = event.target;
    const question = target.closest('[data-question]');
    if (question) {
      const questionNum = parseInt(question.getAttribute('data-question') || '0');
      if (questionNum > 0) {
        this.setCurrentQuestion(questionNum);
      }
    }
  }

  /**
   * Update selected options array
   * Collects all selected values in order of questions
   */
  updateSelectedOptions() {
    const selected = [];
    
    // Get all questions in order
    for (const question of this.questions) {
      const questionInputs = question.querySelectorAll('input[type="radio"]:checked, input[type="checkbox"]:checked');
      
      // For single-select (radio), only one should be checked
      // For multi-select (checkbox), multiple can be checked
      for (const input of questionInputs) {
        selected.push(input.value);
      }
    }
    
    this.selectedOptions = selected;
  }
  
  /**
   * Update data-selected states on option labels for styling
   */
  updateOptionStates() {
    const allLabels = this.querySelectorAll('.quiz-modal__option');
    for (const label of allLabels) {
      // Find the associated input using the 'for' attribute
      const labelFor = label.getAttribute('for');
      if (labelFor) {
        const input = document.getElementById(labelFor);
        if (input && input.checked) {
          label.setAttribute('data-selected', 'true');
        } else {
          label.removeAttribute('data-selected');
        }
      }
    }
  }

  /**
   * Update Question 2 media based on Question 1 (skin tone) selection
   * Looks for conditional media data attributes and switches images/videos accordingly
   */
  updateQuestion2Media() {
    // Get Question 1 (skin tone question)
    const question1 = this.questions[0];
    if (!question1) return;

    // Get selected value from Question 1
    const question1Input = question1.querySelector('input[type="radio"]:checked, input[type="checkbox"]:checked');
    if (!question1Input) return;

    const skinToneValue = this.normalizeValue(question1Input.value);
    
    // Get Question 2
    const question2 = this.questions[1];
    if (!question2) return;

    // Determine which media field to use based on skin tone
    let mediaTypeAttr = 'data-default-media-type';
    let mediaSrcAttr = 'data-default-media-src';
    
    if (skinToneValue.includes('fair')) {
      mediaTypeAttr = 'data-media-fair-type';
      mediaSrcAttr = 'data-media-fair-src';
    } else if (skinToneValue.includes('medium')) {
      mediaTypeAttr = 'data-media-medium-type';
      mediaSrcAttr = 'data-media-medium-src';
    } else if (skinToneValue.includes('dark')) {
      mediaTypeAttr = 'data-media-dark-type';
      mediaSrcAttr = 'data-media-dark-src';
    }

    // Update all Question 2 option images
    const question2ImageContainers = question2.querySelectorAll('.quiz-modal__option-image[data-default-media-type]');
    
    for (const container of question2ImageContainers) {
      const mediaType = container.getAttribute(mediaTypeAttr);
      const mediaSrc = container.getAttribute(mediaSrcAttr);
      
      // If conditional media exists, use it; otherwise fall back to default
      const finalMediaType = mediaType || container.getAttribute('data-default-media-type');
      const finalMediaSrc = mediaSrc || container.getAttribute('data-default-media-src');
      
      if (!finalMediaType || !finalMediaSrc) continue;

      // Find existing media element
      const existingMedia = container.querySelector('[data-media-element]');
      
      if (finalMediaType === 'image') {
        // Update or create image element
        if (existingMedia && existingMedia.tagName === 'IMG') {
          existingMedia.src = finalMediaSrc;
        } else {
          // Remove existing video if present
          if (existingMedia) {
            existingMedia.remove();
          }
          // Create new image
          const img = document.createElement('img');
          img.src = finalMediaSrc;
          img.alt = container.closest('.quiz-modal__option')?.querySelector('.quiz-modal__option-label')?.textContent || '';
          img.loading = 'lazy';
          img.width = 184;
          img.height = 230;
          img.setAttribute('data-media-element', '');
          container.appendChild(img);
        }
      } else if (finalMediaType === 'video') {
        // Update or create video element
        if (existingMedia && existingMedia.tagName === 'VIDEO') {
          existingMedia.src = finalMediaSrc;
        } else {
          // Remove existing image if present
          if (existingMedia) {
            existingMedia.remove();
          }
          // Create new video
          const video = document.createElement('video');
          video.src = finalMediaSrc;
          video.muted = true;
          video.loop = true;
          video.playsInline = true;
          video.width = 184;
          video.height = 230;
          video.setAttribute('data-media-element', '');
          container.appendChild(video);
        }
      }
    }
  }

  /**
   * Navigate to a specific question
   */
  goToQuestion(index) {
    if (index < 1 || index > this.questions.length) return;
    
    // Get next question
    const nextQuestion = this.questions[index - 1];
    
    // Hide all questions
    this.questions.forEach(q => {
      q.style.display = 'none';
      // Add offscreen class to hide from animation observer
      q.classList.add('scroll-trigger--offscreen');
    });
    
    // Show next question
    nextQuestion.style.display = 'flex';
    
    // Trigger animation by removing offscreen class
    // The animate snippet already adds scroll-trigger animate--slide-in classes
    nextQuestion.classList.remove('scroll-trigger--offscreen');
    
    // Force reflow to trigger animation
    void nextQuestion.offsetHeight;
    
    this.setCurrentQuestion(index);
    
    // Update Question 2 media if navigating to Question 2
    if (index === 2) {
      this.updateQuestion2Media();
    }
    
    // Focus first input in the question
    const firstInput = nextQuestion.querySelector('input[type="radio"]:checked, input[type="checkbox"]:checked') || 
                      nextQuestion.querySelector('input[type="radio"], input[type="checkbox"]');
    if (firstInput) {
      firstInput.focus();
    }
  }

  /**
   * Set current question and update UI
   */
  setCurrentQuestion(index) {
    this.currentQuestion = index;
    this.setAttribute('data-current-question', index.toString());

    // Update current question display (find the progress text element)
    const progressText = this.querySelector('.quiz-modal__progress-text [data-current-question]');
    if (progressText) {
      progressText.textContent = index;
    }

    // Update progress bar
    this.updateProgress();

    // Toggle back button state
    this.toggleBackButtons();
  }
  
  /**
   * Reset quiz to initial state
   */
  reset() {
    this.currentQuestion = 1;
    this.selectedOptions = [];
    this.setCurrentQuestion(1);
    
    // Re-load result data in case it was cleared
    this.loadResultData();
    
    // Clear all form inputs
    const inputs = this.querySelectorAll('input[type="radio"], input[type="checkbox"]');
    inputs.forEach(input => {
      input.checked = false;
    });
    
    // Reset first radio button to checked state for single-select questions
    const firstRadios = this.querySelectorAll('input[type="radio"]');
    for (const radio of firstRadios) {
      const question = radio.closest('[data-question]');
      if (question) {
        const questionNum = parseInt(question.getAttribute('data-question') || '0');
        if (questionNum === 1) {
          const firstRadioInQuestion = question.querySelector('input[type="radio"]');
          if (firstRadioInQuestion) {
            firstRadioInQuestion.checked = true;
            break;
          }
        }
      }
    }
    
    // Reset first checkbox to checked state for multi-select questions
    const firstCheckboxes = this.querySelectorAll('input[type="checkbox"]');
    for (const checkbox of firstCheckboxes) {
      const question = checkbox.closest('[data-question]');
      if (question && question.hasAttribute('data-multi-select')) {
        const questionNum = parseInt(question.getAttribute('data-question') || '0');
        if (questionNum === 1) {
          const firstCheckboxInQuestion = question.querySelector('input[type="checkbox"]');
          if (firstCheckboxInQuestion) {
            firstCheckboxInQuestion.checked = true;
            break;
          }
        }
      }
    }
    
    // Update option states
    this.updateOptionStates();
  }

  /**
   * Update progress bar
   * Adds 'is-active' class to steps up to and including current question
   */
  updateProgress() {
    const progressSteps = this.querySelectorAll('.quiz-modal__progress-step');
    progressSteps.forEach((step, index) => {
      if (index < this.currentQuestion) {
        step.classList.add('is-active');
      } else {
        step.classList.remove('is-active');
      }
    });
  }

  /**
   * Toggle back button disabled state
   */
  toggleBackButtons() {
    // Search in entire document since button is in header outside quiz-component
    const backButtons = document.querySelectorAll('[data-quiz-back]');
    for (const button of backButtons) {
      if (this.currentQuestion === 1) {
        button.style.display = 'none';
        button.setAttribute('disabled', '');
      } else {
        button.style.display = 'flex';
        button.removeAttribute('disabled');
      }
    }
  }

  /**
   * Handle form submission
   */
  handleSubmit() {
    const submitButton = this.querySelector('[data-quiz-submit]');
    
    // Set loading state with spinner
    if (submitButton) {
      submitButton.setAttribute('aria-busy', 'true');
      submitButton.setAttribute('disabled', '');
      submitButton.classList.add('loading');
      submitButton.dataset.originalText = submitButton.textContent.trim();
      submitButton.innerHTML = '<span class="quiz-modal__button-loading"></span>';
    }
    
    this.updateSelectedOptions();
    
    // Find matching result
    const result = this.findMatchingResult(this.selectedOptions);
    
    if (!result) {
      console.warn('No matching result found for selections:', this.selectedOptions);
      
      // Remove loading state
      if (submitButton) {
        submitButton.removeAttribute('aria-busy');
        submitButton.removeAttribute('disabled');
        submitButton.classList.remove('loading');
        submitButton.textContent = submitButton.dataset.originalText || 'SEE RESULTS';
      }
      
      // Dispatch event for no results
      this.dispatchEvent(new CustomEvent('quiz:no-results', {
        detail: {
          selections: this.selectedOptions
        },
        bubbles: true
      }));
      return;
    }

    // Set progress to complete
    const progressFill = this.querySelector('[data-progress-fill]');
    if (progressFill) {
      progressFill.style.setProperty('--progress', '100%');
    }

    // Get title for result page
    const titleEl = this.querySelector('[data-title]');
    const title = titleEl ? titleEl.textContent.trim() : 'Your Results';

    // Dispatch event with result - quiz-popup-stub will show results with email capture at top
    this.dispatchEvent(new CustomEvent('quiz:complete', {
      detail: {
        result: result,
        selections: this.selectedOptions,
        title: title
      },
      bubbles: true
    }));
  }

  /**
   * Find matching result based on user selections
   * Supports multi-select questions where multiple paths can match
   */
  findMatchingResult(userOptions) {
    // Re-load result data if it's missing (in case script tag was re-added or component was restored)
    if (!this.resultData || this.resultData.length === 0) {
      this.loadResultData();
    }
    
    if (!this.resultData || this.resultData.length === 0) {
      console.warn('No result data available - quiz results may not be configured');
      return null;
    }

    if (!userOptions || userOptions.length === 0) {
      console.warn('No user options selected');
      return null;
    }

    // Find all results that match user's selections
    const matchingResults = this.findMatchingResults(userOptions);
    
    if (matchingResults.length === 0) {
      console.warn('No matching results found for:', userOptions);
      return null;
    }

    // If multiple results match, aggregate them
    if (matchingResults.length === 1) {
      return matchingResults[0];
    }

    return this.aggregateResults(matchingResults);
  }

  /**
   * Find all results matching user's selections
   * For multi-select questions, matches if ANY selected option matches
   */
  findMatchingResults(userOptions) {
    const matchingResults = [];
    console.log(userOptions, this.resultData);

    for (const result of this.resultData) {
      // Check if any response path matches user's selections
      const hasMatch = result.responses.some(response => {
        return this.responseMatches(response, userOptions);
      });

      if (hasMatch) {
        matchingResults.push(result);
      }
    }

    return matchingResults;
  }

  /**
   * Check if a response path matches user's selections
   * Handles both single-select and multi-select questions
   * 
   * For single-select: response value must match user's selection at that position
   * For multi-select: response value must be in user's selections for that position
   * 
   * Note: Response logic may have fewer values than user selections if some questions are multi-select
   * We need to match response values to user selections in order, skipping multi-select questions
   * where the response logic doesn't specify all selected values
   */
  /**
   * Normalize a value for comparison (handleize)
   */
  normalizeValue(value) {
    if (!value) return '';
    return String(value).toLowerCase().trim().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
  }

  responseMatches(response, userOptions) {
    // Build a map of user options by question position (normalized)
    const userOptionsByPosition = [];
    const questionTypes = [];
    
    for (const question of this.questions) {
      const questionInputs = question.querySelectorAll('input[type="radio"]:checked, input[type="checkbox"]:checked');
      const selectedValues = Array.from(questionInputs).map(input => this.normalizeValue(input.value));
      const isMultiSelect = question.hasAttribute('data-multi-select');
      
      userOptionsByPosition.push(selectedValues);
      questionTypes.push(isMultiSelect);
    }

    // Response array should have same length as number of questions
    // Match each response value to the corresponding question position
    if (response.length !== userOptionsByPosition.length) {
      return false;
    }

    for (let i = 0; i < response.length; i++) {
      const responseValue = this.normalizeValue(response[i]);
      const userSelections = userOptionsByPosition[i] || [];
      const isMultiSelect = questionTypes[i];

      if (isMultiSelect) {
        // For multi-select, check if response value is in user's selections
        if (!userSelections.includes(responseValue)) {
          return false;
        }
      } else {
        // For single-select, must match exactly (user should have exactly one selection)
        if (userSelections.length !== 1 || userSelections[0] !== responseValue) {
          return false;
        }
      }
    }

    return true;
  }

  /**
   * Aggregate multiple results into one
   * Combines products from all matching results
   */
  aggregateResults(results) {
    if (results.length === 0) {
      return null;
    }

    if (results.length === 1) {
      return results[0];
    }

    // Combine all response paths
    const allResponses = [];
    for (const result of results) {
      if (result.responses && Array.isArray(result.responses)) {
        allResponses.push(...result.responses);
      }
    }

    // Combine products if available (use Set to avoid duplicates by product ID)
    const productMap = new Map();
    for (const result of results) {
      if (result.products && Array.isArray(result.products)) {
        for (const product of result.products) {
          // Use product ID or handle as unique key
          const key = product.id || product.handle || JSON.stringify(product);
          if (!productMap.has(key)) {
            productMap.set(key, product);
          }
        }
      }
    }

    // Combine titles
    const titles = results
      .map(r => r.title)
      .filter(Boolean)
      .join(' & ');

    // Use first result's URL (or could use a special aggregated result URL)
    return {
      title: titles || results[0].title || 'Your Results',
      url: results[0].url || '',
      responses: allResponses,
      products: Array.from(productMap.values())
    };
  }
}

// Register the custom element
if (!customElements.get('quiz-component')) {
  customElements.define('quiz-component', QuizComponent);
}

